'use client';

import { FC, Suspense, useEffect, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import { useShallow } from 'zustand/react/shallow';

import { SelectDepositContent } from './SelectDepositContent';
import { Wallet } from '@/api/auth';
import { amountWithCurrencySymbol, getCurrencyName } from '@/helpers/currency';
import { useComponentVisible } from '@/hooks/visibility';
import { useCommonStore, useDepositStore } from '@/store';

import { ScSelectDeposit, ScSelectDepositProps } from './styled';
import { Spinner } from '@/styles/common';

type SelectDepositProps = Omit<ScSelectDepositProps, 'active'> & {
  noEditCurrencyBtn?: boolean;
  togglePreventValidation?: (arg: boolean) => void;
};

export const SelectDeposit: FC<SelectDepositProps> = ({
  isInputAddon,
  withoutSum,
  noEditCurrencyBtn,
  togglePreventValidation,
  ...props
}) => {
  const { images, grayLightColor, mainColor, whiteColor } = useTheme();

  const [checkUserData, toggleCheckUserData] = useState(false);
  const setOriginalAmount = useDepositStore.use.setOriginalAmount();
  const {
    currentWallet,
    changeUserWallet,
    pendingUser,
    pendingWallets,
    currencyConversion,
  } = useCommonStore(
    useShallow((state) => ({
      currentWallet: state.currentWallet,
      changeUserWallet: state.changeUserWallet,
      pendingUser: state.pendingUser,
      pendingWallets: state.pendingWallets,
      currencyConversion: state.currencyConversion,
    }))
  );

  const { ref, openerRef, isComponentVisible, setIsComponentVisible } =
    useComponentVisible(false);

  const pending = useMemo(
    () => pendingUser || pendingWallets,
    [pendingUser, pendingWallets]
  );

  const handleOpen = () => {
    setIsComponentVisible(!isComponentVisible);
  };

  const handleChange = async (currency: Wallet['currency']) => {
    handleOpen();
    await changeUserWallet(currency);
    setOriginalAmount('');
    toggleCheckUserData(true);
  };

  useEffect(() => {
    if (!pending && checkUserData) {
      toggleCheckUserData(false);
    }
  }, [pending, checkUserData, toggleCheckUserData]);

  useEffect(() => {
    if (!togglePreventValidation) return;

    togglePreventValidation(isComponentVisible);
  }, [isComponentVisible, togglePreventValidation]);

  if (!currentWallet) return null;

  return (
    <ScSelectDeposit
      active={isComponentVisible}
      isInputAddon={isInputAddon}
      {...props}
    >
      <button
        ref={openerRef}
        className="select-button"
        onClick={handleOpen}
        disabled={pending}
        type="button"
      >
        {pending && !isInputAddon ? (
          <Spinner
            size={20}
            center
            color={mainColor}
            background={isInputAddon ? whiteColor : grayLightColor}
          />
        ) : (
          <>
            <div className="value">
              {withoutSum
                ? currencyConversion(
                    currentWallet.amountCents,
                    getCurrencyName(currentWallet.currency)
                  )
                : amountWithCurrencySymbol(
                    getCurrencyName(currentWallet.currency),
                    currentWallet.amountCents
                  )}
            </div>
            <div className="arrow">
              <images.arrowDownGrayIcon.component />
            </div>
          </>
        )}
      </button>
      {isComponentVisible && (
        <Suspense>
          <SelectDepositContent
            ref={ref}
            mode={props.mode}
            isInputAddon={isInputAddon}
            noEditCurrencyBtn={noEditCurrencyBtn}
            onOpen={handleOpen}
            onChange={handleChange}
          />
        </Suspense>
      )}
    </ScSelectDeposit>
  );
};
